/**
 * LazySection — Phase 9.3: Progressive Form Loading
 *
 * Uses IntersectionObserver to defer rendering of off-screen form sections.
 * Sections render a lightweight placeholder until they scroll into view,
 * then mount the full DynamicSection component.
 *
 * This dramatically improves initial render time on forms with 10+ sections
 * (common in compliance / audit operational forms).
 *
 * Usage:
 *   <LazySection rootMargin="200px">
 *     <DynamicSection ... />
 *   </LazySection>
 *
 * @phase 9.3 - Progressive Form Loading
 */

'use client';

import React, { useState, useRef, useEffect, type ReactNode } from 'react';

interface LazySectionProps {
  /** The section content to render lazily */
  children: ReactNode;
  /** Placeholder height when section is not yet in view. Default: 120 */
  placeholderHeight?: number;
  /** IntersectionObserver rootMargin (pre-load area). Default: '200px' */
  rootMargin?: string;
  /** Section title shown in the placeholder */
  title?: string;
  /** Whether to skip lazy loading (render immediately). Default: false */
  eager?: boolean;
  /** Optional className for the wrapper */
  className?: string;
}

export function LazySection({
  children,
  placeholderHeight = 120,
  rootMargin = '200px',
  title,
  eager = false,
  className,
}: LazySectionProps) {
  const [isVisible, setIsVisible] = useState(eager);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (eager || isVisible) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: just render immediately if no IO support
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    const el = sentinelRef.current;
    if (el) observer.observe(el);

    return () => observer.disconnect();
  }, [eager, isVisible, rootMargin]);

  if (isVisible) {
    return <div className={className}>{children}</div>;
  }

  // Placeholder
  return (
    <div
      ref={sentinelRef}
      className={`animate-pulse rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 ${className || ''}`}
      style={{ minHeight: `${placeholderHeight}px` }}
    >
      <div className="flex items-center gap-3 p-4">
        <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700" />
        {title && (
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">{title}</span>
        )}
      </div>
    </div>
  );
}

export default LazySection;
